import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { RouterService } from "../../../../shared/_services/router.service";
import {
    ListTradingSessionsResponse,
    TradingSession,
    TradingSessionsService,
    TradingSessionEntry,
} from "./trading-sessions.service";
import { PageEvent } from "@angular/material/paginator";
import { ScheduleTypes } from "../../../../shared/_enums/schedule-types.enum";

type TradingSessionWithDetails = TradingSession & {
    entries: TradingSessionEntry[];
    isOpenned: boolean;
    isLoaded: boolean;
};
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-trading-sessions",
    templateUrl: "./trading-sessions.component.html",
    styleUrl: "./trading-sessions.component.scss",
    standalone: false,
})
export class TradingSessionsComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject<any>();

    tradingSessions: TradingSessionWithDetails[] = [];
    page: number = 0;
    pageSize: number = 10;
    totalTradingSessions: number = 0;
    isLoaded: boolean = false;

    startDate = signal(new Date(Date.now() - 1000 * 60 * 60 * 24 * 7));
    endDate = signal(new Date());

    allScheduleTypes = Object.entries(ScheduleTypes)
        .filter(([, value]) => typeof value === "number")
        .map(([key, value]) => ({ label: key, value: value as number }));

    selectedScheduleTypes = signal<number[]>(
        this.allScheduleTypes.filter((t) => t.value !== ScheduleTypes.None).map((t) => t.value),
    );

    userId = signal("");
    userName = signal("");
    botId = signal("");
    botName = signal("");
    returnEmptySessions = signal(false);

    constructor(
        private router: RouterService,
        private tradingSessionsService: TradingSessionsService,
    ) {}

    ngOnInit(): void {
        this.pageChanged({ pageIndex: this.page, pageSize: this.pageSize } as PageEvent);
    }

    ngOnDestroy(): void {
        // prevent memory leak when component destroyed
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    pageChanged(e: PageEvent): void {
        this.tradingSessionsService
            .list({
                page: e.pageIndex + 1,
                itemsPerPage: e.pageSize,
                startDate: this.getDateString(this.startDate()),
                endDate: this.getDateString(this.endDate()),
                scheduleTypes: this.selectedScheduleTypes().length > 0 ? this.selectedScheduleTypes() : undefined,
                userId: this.trimmedOrUndefined(this.userId()),
                userName: this.trimmedOrUndefined(this.userName()),
                botId: this.trimmedOrUndefined(this.botId()),
                botName: this.trimmedOrUndefined(this.botName()),
                returnEmptySessions: this.returnEmptySessions(),
            })
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: ListTradingSessionsResponse) => {
                this.tradingSessions = response.tradingSessions.map((session: TradingSession) => ({
                    ...session,
                    entries: [],
                    isOpenned: false,
                    isLoaded: false,
                }));
                this.totalTradingSessions = response.total;
                this.page = e.pageIndex;
                this.isLoaded = true;
            });
    }

    onStartChanged(): void {
        if (this.startDate() <= this.endDate()) {
            return;
        }

        this.endDate.set(new Date(this.startDate().getTime() + 1000 * 60 * 60 * 24 * 7));
    }

    onEndChanged(): void {
        if (this.startDate() <= this.endDate()) {
            return;
        }

        this.startDate.set(new Date(this.endDate().getTime() - 1000 * 60 * 60 * 24 * 7));
    }

    search(): void {
        this.page = 0;
        this.pageChanged({ pageIndex: this.page, pageSize: this.pageSize } as PageEvent);
    }

    private trimmedOrUndefined(value: string): string | undefined {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }

    private getDateString(date: Date): string {
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    }

    toggleDetails(tradingSession: TradingSessionWithDetails): void {
        if (tradingSession.isOpenned) {
            tradingSession.isOpenned = false;
            return;
        }
        if (tradingSession.isLoaded) {
            tradingSession.isOpenned = true;
            return;
        }
        this.tradingSessionsService
            .listEntries(tradingSession.id)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((entries) => {
                tradingSession.entries = entries;
                tradingSession.isOpenned = true;
                tradingSession.isLoaded = true;
            });
    }

    downloadEntryLogs(tradingSession: TradingSessionWithDetails, entry: TradingSessionEntry): void {
        this.tradingSessionsService
            .getEntryLogs(tradingSession.id, entry.id)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((blob) => {
                this.downloadFile(blob, `trading-session-${tradingSession.id}-entry-${entry.botId}-logs.zip`);
            });
    }

    downloadSessionLogs(tradingSession: TradingSessionWithDetails): void {
        this.tradingSessionsService
            .getSessionLogs(tradingSession.id)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((blob) => {
                this.downloadFile(blob, `trading-session-${tradingSession.id}-logs.zip`);
            });
    }

    private downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
}
